/**
 * The host-registered opener kinds, apart from the built-in spinner in `registry.ts`. The kind
 * entry points (`@loot-os/react/neon-slam`, `/neon-pack`, `/retail-box`, `/openers`) import this
 * module and never the registry, so registering a kind pulls in neither the spinner nor any CSS:
 * the registering entry stays a few hundred bytes and the scene with its stylesheet loads on first
 * use.
 */
import { type ComponentType, lazy } from "react";
import {
  defineOpenerDescriptor,
  type OpenerComponentProps,
  type OpenerDescriptor,
} from "../core/contract";
import type { OpenerTokenFallback } from "../theme/theme";

/**
 * Every animation a box's opener style can name. A hand-kept mirror of `OPENER_KINDS` in
 * `@loot-os/api-contract` (the published types cannot reference that private package);
 * `registry.test.ts` fails when the two drift.
 */
export type OpenerKind = "spinner" | "neon_slam" | "neon_pack" | "retail_box";

const registered = new Map<string, OpenerDescriptor>();

/** Makes a kind this entry point does not bundle available to every opener on the page. */
export function registerOpener(kind: OpenerKind, descriptor: OpenerDescriptor): void {
  registered.set(kind, descriptor);
}

/** The descriptor a host registered for `kind`, if any. */
export function getRegisteredOpener(kind: string): OpenerDescriptor | undefined {
  return registered.get(kind);
}

/**
 * The descriptor of a heavy, stage-owning opener on its own entry point: one scene from idle
 * through the result with the shell's controls docked inside it, one item per open (the quantity
 * selector is hidden), holding on the reveal itself before it hands back. The scene loads on first
 * use or on `preload`.
 */
export function defineStageOpener<T>(
  load: () => Promise<{ default: ComponentType<OpenerComponentProps<T>> }>,
  tokens: Readonly<Record<string, OpenerTokenFallback>>,
): OpenerDescriptor {
  return defineOpenerDescriptor({
    Opener: lazy(load),
    preload: () => {
      void load();
    },
    stage: "all",
    loopTarget: "loop",
    supportsBulk: false,
    revealExit: { lingerMs: { normal: 0, quick: 0 } },
    tokens,
  });
}
